import { useDisclosure } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Modal } from "@msuite/ui/modal";
import { DataTable } from "@msuite/ui/table";
import { guardArray } from "@msuite/utils/array";
import type { SubstitutionPlanFrontendFormSchema } from "@msuite/utils/schemas";
import {
	type SubstitutionPlanValidation,
	validateSubstitutionPlanItem,
} from "@msuite/utils/substitutions";
import { useFormContext, useWatch } from "react-hook-form";
import { SubstitutionPlanFormMenu } from "@/forms/substitution-plan-form/substitution-plan-form-menu";
import { SubstitutionPlanOverview } from "@/forms/substitution-plan-form/substitution-plan-overview";
import { SubstitutionPlanFormContext } from "./context";
import { columns } from "./table-config";

/** Props Interface */
interface SubstitutionPlanFormProps {
	startDate: string;
	endDate: string;
	absenceId: string;
	validation: SubstitutionPlanValidation;
}

export function SubstitutionPlanForm({
	startDate,
	endDate,
	absenceId,
	validation,
}: SubstitutionPlanFormProps) {
	/** Form */
	const methods = useFormContext<SubstitutionPlanFrontendFormSchema>();
	const items = useWatch({ control: methods.control, name: "items" });

	/** Hooks */
	const { t } = useTranslation();
	const unansweredDisclosure = useDisclosure();

	/** Functions */
	function getItemIndex(itemId: string) {
		return items.findIndex((item) => item.id === itemId);
	}

	function handleOnMuteAllContacts() {
		const items = methods.getValues("items");
		for (let i = 0; i < items.length; i++) {
			methods.setValue(`items.${i}.contactNotifications`, []);
		}
	}

	/** Render */
	return (
		<SubstitutionPlanFormContext.Provider
			value={{ getItemIndex, startDate, endDate, absenceId, validation }}
		>
			<Modal.TableWrapper>
				<DataTable
					data={guardArray(items)}
					columns={columns}
					isLoading={methods.formState.isLoading}
					persistenceKey="substitution-plan-form-table"
					menu={<SubstitutionPlanFormMenu unansweredDisclosure={unansweredDisclosure} />}
					refetch={undefined}
					queryKey={undefined}
					initialPaginationState={{
						pageIndex: 1,
						pageSize: 100,
					}}
					initialPinningState={{
						start: ["sub-row-handle", "projectNumber"],
						end: [
							"substitution-picker",
							"notify-contacts-picker",
							"is-assignment-not-required",
							"quick-notes",
							"hints",
						],
					}}
					enableRowSelection
					enableMultiRowSelection
					menuItems={[
						{
							label: t("substitutions.mute_all_contacts"),
							icon: icon("PhoneOff"),
							onClick: handleOnMuteAllContacts,
						},
					]}
					slots={{
						"below-search": () => <SubstitutionPlanOverview />,
					}}
					injectRowFilter={(rows) => {
						if (!unansweredDisclosure.isOpen) return rows;
						return rows.filter((row) => !validateSubstitutionPlanItem(row.original));
					}}
				/>
			</Modal.TableWrapper>
		</SubstitutionPlanFormContext.Provider>
	);
}
